import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const PAGE_NAMES = {
  '/': 'Landing',
  '/login': 'Login',
  '/register': 'Register',
  '/forgot-password': 'Forgot Password',
  '/reset-password': 'Reset Password',
  // '/plans': retired (free marketplace),
  '/search': 'Public Search',
  '/faq': 'FAQ',
  '/help': 'Help Centre',
  '/contact': 'Contact',
  '/report-bug': 'Report a Bug',
  '/settings': 'User Settings',
  '/messages': 'Messages',
  '/terms': 'Terms of Service',
  '/privacy': 'Privacy Policy',
  '/employer/dashboard': 'Employer Dashboard',
  '/tradesperson/dashboard': 'Tradesperson Dashboard',
  '/admin/dashboard': 'Admin Dashboard',
  '/admin/verification-queue': 'Admin Verification Queue',
  '/admin/settings': 'Admin Settings',
  '/admin/user-control': 'Admin User Control',
  '/admin/users': 'Admin User Management',
  '/admin/regions': 'Admin Expansion',
  '/admin/waitlist': 'Admin Waitlist',
};

function getPageName(pathname) {
  if (PAGE_NAMES[pathname]) return PAGE_NAMES[pathname];
  if (pathname.startsWith('/profile/')) return 'Tradesperson Profile';
  if (pathname.startsWith('/admin/user/')) return 'Admin User Profile';
  if (pathname.startsWith('/admin/')) return 'Admin';
  if (pathname.startsWith('/employer/')) return 'Employer';
  if (pathname.startsWith('/tradesperson/')) return 'Tradesperson';
  return pathname;
}

function getEnvironment() {
  const host = window.location.hostname;
  if (host.includes('preview.emergentagent') || host.includes('localhost')) return 'Preview';
  return 'Production';
}

/**
 * Normalise a UK phone number into the digits-only international format that
 * wa.me expects (no "+", no spaces).
 *   "07487567788"     -> "447487567788"
 *   "+447487567788"   -> "447487567788"
 *   "447487567788"    -> "447487567788"
 *   "+1 555 123 4567" -> "15551234567" (generic fallback)
 */
export function normaliseWhatsAppNumber(raw) {
  if (!raw) return '';
  const stripped = String(raw).replace(/[^0-9+]/g, '');
  if (!stripped) return '';
  // International format already
  if (stripped.startsWith('+')) return stripped.slice(1);
  // UK trunk prefix: 07xxx... -> 447xxx
  if (stripped.startsWith('0') && stripped.length >= 10) return '44' + stripped.slice(1);
  // Assume already international digits (e.g. starts with 44 or another country code)
  return stripped;
}

/**
 * Build a WhatsApp URL with prefilled support message.
 * @param {object} config - { phone_number, intro_message }
 * @param {object|null} user - authenticated user or null
 * @param {string} pathname - current route path
 * @returns {string} WhatsApp URL
 */
export function buildWhatsAppUrl(config, user, pathname) {
  if (!config?.phone_number) return '';

  const phone = normaliseWhatsAppNumber(config.phone_number);
  if (!phone) return '';
  const intro = config.intro_message || 'Hi OnSiteHand support, I need help.';
  const pageName = getPageName(pathname);
  const pageUrl = window.location.href;
  const env = getEnvironment();

  let lines = [intro, ''];

  if (user && user.user_id) {
    const role = user.user_type || 'user';
    lines.push(`Support Ref: USER_${user.user_id}_${role}`);
    if (user.name) lines.push(`Name: ${user.name}`);
    if (user.email) lines.push(`Email: ${user.email}`);
    lines.push(`Role: ${role}`);
    lines.push(`User ID: ${user.user_id}`);
    if (role === 'employer' && user.company_name) lines.push(`Company: ${user.company_name}`);
    if (role === 'tradesperson' && user.trade) lines.push(`Trade: ${user.trade}`);
  } else {
    lines.push('Support Ref: GUEST');
    lines.push('Name: Guest user');
  }

  lines.push(`Page: ${pageName}`);
  lines.push(`URL: ${pageUrl}`);
  lines.push(`Environment: ${env}`);
  lines.push('');
  lines.push('Please help me with:');

  const text = encodeURIComponent(lines.join('\n'));
  return `https://wa.me/${phone}?text=${text}`;
}

/**
 * Hook: returns a function to open WhatsApp support.
 * Requires waConfig ({ phone_number, intro_message }) and optionally user from auth context.
 */
export function useWhatsAppSupport(waConfig, user) {
  const location = useLocation();

  const openSupport = useCallback(() => {
    if (!waConfig?.phone_number) return;
    const url = buildWhatsAppUrl(waConfig, user, location.pathname);
    if (url) window.open(url, '_blank', 'noopener');
  }, [waConfig, user, location.pathname]);

  const getSupportUrl = useCallback(() => {
    if (!waConfig?.phone_number) return '';
    return buildWhatsAppUrl(waConfig, user, location.pathname);
  }, [waConfig, user, location.pathname]);

  return { openSupport, getSupportUrl, isAvailable: Boolean(waConfig?.phone_number && waConfig?.enabled) };
}
